/***************************************************************************************
 * FileName      : QuadTextPlane.ts
 * Description   : 4개 꼭짓점 좌표로 정의된 사각형에 텍스트를 렌더링하는 SDK 컴포넌트
 *                 - PlaneGeometry 꼭짓점 위치를 수정하여 임의의 사각형 형태 지원
 *                 - Canvas 2D API로 텍스트 직접 렌더링
 *                 - 텍스트 내용, 폰트, 색상, 정렬 등 스타일 지원
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date :
 * Reference     : QuadImagePlane.ts, CanvasText.ts,
 *                 matterport_mvp1/src/09-text-quad-placement/packages/common/src/sdk-components/QuadTextPlane.ts
 ***************************************************************************************/

import { SceneComponent, ComponentInteractionType } from '../SceneComponent';

// ============================================================================
// 꼭짓점 좌표 타입
// ============================================================================

/** 2026.02.12 : 박은정 : 3D 좌표 */
interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// ============================================================================
// 입력 타입 정의
// ============================================================================

type Inputs = {
  /** 표시할 텍스트 (줄바꿈 지원) */
  text: string;
  /** 폰트 크기 (px) */
  fontSize: number;
  /** 폰트 패밀리 */
  fontFamily: string;
  /** 폰트 굵기 */
  fontWeight: string;
  /** 텍스트 색상 */
  textColor: string;
  /** 배경 색상 */
  backgroundColor: string;
  /** 텍스트 정렬 ('left' | 'center' | 'right') */
  textAlign: string;
  /** 줄 간격 배수 */
  lineHeight: number;
  /** 패딩 (px) */
  padding: number;
  /** 텍스처 가로 해상도 (픽셀) */
  textureWidth: number;
  /** 텍스처 세로 해상도 (픽셀) */
  textureHeight: number;
  /** 좌측 상단 꼭짓점 (로컬 좌표) */
  topLeft: Vec3;
  /** 우측 상단 꼭짓점 (로컬 좌표) */
  topRight: Vec3;
  /** 좌측 하단 꼭짓점 (로컬 좌표) */
  bottomLeft: Vec3;
  /** 우측 하단 꼭짓점 (로컬 좌표) */
  bottomRight: Vec3;
  /** 투명 배경 사용 여부 */
  transparent: boolean;
  /** 불투명도 (0.0 ~ 1.0) */
  opacity: number;
  /** 표시 여부 */
  visible: boolean;
  /** 2026.02.12 : 박은정 : 테두리 표시 여부 */
  showBorder: boolean;
  /** 2026.02.12 : 박은정 : 양면 렌더링 여부 */
  doubleSide: boolean;
};

// ============================================================================
// QuadTextPlane 컴포넌트
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 4개 꼭짓점으로 정의된 사각형에 텍스트를 렌더링
 *
 * 접근 방식:
 *   - PlaneGeometry(1,1) 생성 후 꼭짓점 position 속성을 직접 수정
 *   - Canvas 2D API로 텍스트를 렌더링하여 CanvasTexture로 사용
 *   - 텍스트 내용, 스타일 변경 시 캔버스 재렌더링
 *   - PlaneGeometry 꼭짓점 순서:
 *     vertex 0 = topLeft,     vertex 1 = topRight,
 *     vertex 2 = bottomLeft,  vertex 3 = bottomRight
 */
class QuadTextPlane extends SceneComponent {
  private mesh: any = null;
  private pivotNode: any = null;
  private canvas: HTMLCanvasElement | null = null;
  private canvasTexture: any = null;

  inputs: Inputs = {
    text: '',
    fontSize: 48,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    textColor: '#ffffff',
    // 2026.02.12 : 박은정 : 기본 배경색을 투명으로 변경 (라벨 배치 시 이미지 가림 방지)
    backgroundColor: 'rgba(0, 0, 0, 0)',
    textAlign: 'center',
    lineHeight: 1.4,
    padding: 40,
    textureWidth: 1024,
    textureHeight: 1024,
    topLeft: { x: -0.5, y: 0.5, z: 0 },
    topRight: { x: 0.5, y: 0.5, z: 0 },
    bottomLeft: { x: -0.5, y: -0.5, z: 0 },
    bottomRight: { x: 0.5, y: -0.5, z: 0 },
    transparent: true,
    opacity: 1.0,
    visible: true,
    showBorder: true,
    doubleSide: true,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
  };

  /**
   * 2026.02.12 : 박은정 : 컴포넌트 초기화
   * - PlaneGeometry 생성 후 꼭짓점 위치 수정
   * - Canvas → CanvasTexture → Mesh 생성
   * - outputs.objectRoot 설정
   */
  onInit() {
    const THREE = this.context.three;

    // 2026.02.12 : 박은정 : 오프스크린 캔버스 생성
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.inputs.textureWidth;
    this.canvas.height = this.inputs.textureHeight;

    // 2026.02.12 : 박은정 : 텍스트 렌더링
    this.renderText();

    // 2026.02.12 : 박은정 : CanvasTexture 생성
    this.canvasTexture = new THREE.CanvasTexture(this.canvas);
    // 2026.02.12 : 박은정 : premultiplyAlpha 비활성화 (투명 배경 정확한 렌더링)
    this.canvasTexture.premultiplyAlpha = false;

    // 2026.02.12 : 박은정 : PlaneGeometry 생성
    const geometry = new THREE.PlaneGeometry(1.0, 1.0);

    // 2026.02.12 : 박은정 : 꼭짓점 위치를 4개 꼭짓점 좌표로 수정
    this.updateGeometryVertices(geometry);

    // 2026.02.12 : 박은정 : MeshBasicMaterial (투명 배경 시 뒤 객체 가림 방지)
    const material = new THREE.MeshBasicMaterial({
      map: this.canvasTexture,
      transparent: this.inputs.transparent,
      opacity: this.inputs.opacity,
      side: this.inputs.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
      // 2026.02.12 : 박은정 : 투명 메시가 뒤 객체를 가리지 않도록 depthWrite 비활성화
      depthWrite: !this.inputs.transparent,
      // 2026.02.12 : 박은정 : 완전 투명 픽셀 제거 (알파값 0.01 미만 폐기)
      alphaTest: 0.01,
      // 2026.04.23 : 박은정 : Z-fighting 방지 — 동일 평면의 이미지 Quad와 겹칠 때
      // 텍스트 fragment의 depth를 카메라 쪽으로 편향시켜 항상 텍스트가 앞에 보이도록 함
      // (카메라 이동 시 텍스트가 각도에 따라 사라졌다 나타나는 현상 제거)
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });

    // 2026.02.12 : 박은정 : Mesh 생성
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.visible = this.inputs.visible;
    // 2026.04.23 : 박은정 : 이미지 Quad보다 나중에 렌더링되도록 renderOrder 상향
    // (같은 depth일 때 뒤에 그려지는 메시가 보이므로 텍스트가 항상 위에 오도록 보장)
    this.mesh.renderOrder = 10;

    // 2026.02.12 : 박은정 : 피벗 그룹
    this.pivotNode = new THREE.Group();
    this.pivotNode.add(this.mesh);

    // 2026.02.12 : 박은정 : outputs 설정
    this.outputs.objectRoot = this.pivotNode;
    this.outputs.collider = this.pivotNode;
  }

  /**
   * 2026.02.12 : 박은정 : PlaneGeometry 꼭짓점 위치를 4개 꼭짓점 좌표로 수정
   *
   * THREE.js PlaneGeometry(1,1) 꼭짓점 순서:
   *   vertex 0: (-0.5,  0.5, 0) → topLeft
   *   vertex 1: ( 0.5,  0.5, 0) → topRight
   *   vertex 2: (-0.5, -0.5, 0) → bottomLeft
   *   vertex 3: ( 0.5, -0.5, 0) → bottomRight
   */
  private updateGeometryVertices(geometry: any) {
    const posAttr = geometry.getAttribute('position');
    if (!posAttr) return;

    const { topLeft, topRight, bottomLeft, bottomRight } = this.inputs;

    // 2026.02.12 : 박은정 : 꼭짓점 0: 좌측 상단
    posAttr.setXYZ(0, topLeft.x, topLeft.y, topLeft.z);
    // 2026.02.12 : 박은정 : 꼭짓점 1: 우측 상단
    posAttr.setXYZ(1, topRight.x, topRight.y, topRight.z);
    // 2026.02.12 : 박은정 : 꼭짓점 2: 좌측 하단
    posAttr.setXYZ(2, bottomLeft.x, bottomLeft.y, bottomLeft.z);
    // 2026.02.12 : 박은정 : 꼭짓점 3: 우측 하단
    posAttr.setXYZ(3, bottomRight.x, bottomRight.y, bottomRight.z);

    posAttr.needsUpdate = true;
    geometry.computeVertexNormals();
  }

  /**
   * 2026.02.12 : 박은정 : 입력값 변경 시 처리
   */
  onInputsUpdated(oldInputs: Inputs) {
    // 2026.04.23 : 박은정 : 텍스처 크기 변경 시 캔버스 재생성 + 텍스처 교체
    //   (플레인 비율에 맞춰 캔버스 비율을 바꿔서 텍스트가 늘어나지 않게 함)
    if (
      (oldInputs.textureWidth !== this.inputs.textureWidth ||
        oldInputs.textureHeight !== this.inputs.textureHeight) &&
      this.canvas &&
      this.mesh
    ) {
      this.canvas.width = this.inputs.textureWidth;
      this.canvas.height = this.inputs.textureHeight;
      this.renderText();
      if (this.canvasTexture) {
        this.canvasTexture.needsUpdate = true;
      }
    }

    // 2026.02.12 : 박은정 : 텍스트 또는 스타일 변경 시 캔버스 재렌더링
    if (
      oldInputs.text !== this.inputs.text ||
      oldInputs.fontSize !== this.inputs.fontSize ||
      oldInputs.fontFamily !== this.inputs.fontFamily ||
      oldInputs.fontWeight !== this.inputs.fontWeight ||
      oldInputs.textColor !== this.inputs.textColor ||
      oldInputs.backgroundColor !== this.inputs.backgroundColor ||
      oldInputs.textAlign !== this.inputs.textAlign ||
      oldInputs.lineHeight !== this.inputs.lineHeight ||
      oldInputs.padding !== this.inputs.padding
    ) {
      this.renderText();
    }

    // 2026.02.12 : 박은정 : 꼭짓점 좌표 변경 시 지오메트리 업데이트
    if (
      oldInputs.topLeft !== this.inputs.topLeft ||
      oldInputs.topRight !== this.inputs.topRight ||
      oldInputs.bottomLeft !== this.inputs.bottomLeft ||
      oldInputs.bottomRight !== this.inputs.bottomRight
    ) {
      if (this.mesh) {
        this.updateGeometryVertices(this.mesh.geometry);
      }
    }

    // 2026.02.12 : 박은정 : 머티리얼 속성 업데이트
    if (this.mesh) {
      this.mesh.visible = this.inputs.visible;
      const material = this.mesh.material;
      if (material) {
        material.opacity = this.inputs.opacity;
        material.transparent = this.inputs.transparent;
        material.needsUpdate = true;
      }
    }
  }

  /**
   * 2026.02.12 : 박은정 : 이벤트 전달
   */
  onEvent(eventType: string, eventData: unknown) {
    this.notify(eventType, eventData);
  }

  /**
   * 2026.02.12 : 박은정 : 리소스 정리
   * 2026.04.27 : 박은정 : pivotNode 를 부모 씬에서 명시적으로 detach.
   *   - outputs.objectRoot=null 만으로는 일부 환경에서 메시가 즉시 제거되지 않아
   *     이전 노드의 메시가 잔존 → 같은 위치에 새 노드가 다시 생기면 텍스트 2중 표시 발생.
   *   - parent.remove()로 THREE.js 씬 그래프에서 직접 떼어내 고스트 메시를 제거.
   */
  onDestroy() {
    if (this.pivotNode && this.pivotNode.parent) {
      this.pivotNode.parent.remove(this.pivotNode);
    }

    this.outputs.collider = null;
    this.outputs.objectRoot = null;

    if (this.canvasTexture) {
      this.canvasTexture.dispose();
      this.canvasTexture = null;
    }
    if (this.mesh) {
      this.mesh.material.dispose();
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    this.pivotNode = null;
    this.canvas = null;
  }

  // ==========================================================================
  // 텍스트 렌더링
  // ==========================================================================

  /**
   * 2026.02.12 : 박은정 : Canvas 2D API로 텍스트 렌더링
   * - 배경 그리기
   * - 줄바꿈 처리
   * - 텍스트 정렬 (left, center, right)
   * - 자동 줄바꿈 (캔버스 폭 초과 시)
   */
  private renderText() {
    if (!this.canvas) return;

    // 2026.04.23 : 박은정 : 그리기 전에 캔버스가 현재 fontSize를 담기에 충분한지 검사.
    //   부족하면 비율 유지한 채 확대 (최대 4096). fontSize 변경 시 상단/하단 잘림 방지.
    {
      const fz = this.inputs.fontSize;
      const lh = this.inputs.lineHeight;
      const pd = this.inputs.padding;
      const tx = this.inputs.text;
      const numLines = Math.max(1, (tx || '').split('\n').length);
      const requiredH = Math.ceil(numLines * fz * lh + pd * 2);
      if (this.canvas.height < requiredH) {
        const HARD_MAX = 4096;
        const scale = requiredH / this.canvas.height;
        const newH = Math.min(HARD_MAX, Math.ceil(this.canvas.height * scale));
        const newW = Math.min(HARD_MAX, Math.ceil(this.canvas.width * scale));
        this.canvas.width = newW;
        this.canvas.height = newH;
      }
    }

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    const w = this.canvas.width;
    const h = this.canvas.height;
    const {
      text,
      fontSize,
      fontFamily,
      fontWeight,
      textColor,
      backgroundColor,
      textAlign,
      lineHeight,
      padding,
    } = this.inputs;

    // 2026.02.12 : 박은정 : 캔버스 초기화 (투명 상태로 리셋)
    ctx.clearRect(0, 0, w, h);

    // 2026.02.12 : 박은정 : 배경색이 투명이 아닌 경우에만 배경 그리기
    // rgba(0,0,0,0) 등 완전 투명 배경일 때는 clearRect 상태를 유지하여 뒤 이미지가 보이도록 함
    if (backgroundColor && !this.isFullyTransparent(backgroundColor)) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, w, h);
    }

    // 2026.02.12 : 박은정 : 테두리 그리기 (showBorder가 true일 때만)
    if (this.inputs.showBorder) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 3;
      ctx.strokeRect(2, 2, w - 4, h - 4);
    }

    // 2026.02.12 : 박은정 : 텍스트 없으면 플레이스홀더
    if (!text || text.trim() === '') {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = `${fontWeight} ${Math.floor(fontSize * 0.8)}px ${fontFamily}`;
      ctx.textAlign = 'center' as CanvasTextAlign;
      ctx.textBaseline = 'middle';
      ctx.fillText('텍스트 없음', w / 2, h / 2);

      if (this.canvasTexture) {
        this.canvasTexture.needsUpdate = true;
      }
      return;
    }

    // 2026.02.12 : 박은정 : 폰트 설정
    // 2026.04.23 : 박은정 : textBaseline을 'middle'로 변경
    //   (top 기준은 폰트 메트릭에 따라 상단 안전 마진이 부족해 잘림 발생.
    //    middle 기준은 Y 좌표가 글자 수직 중앙이라 안정적.)
    ctx.fillStyle = textColor;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textAlign = textAlign as CanvasTextAlign;
    ctx.textBaseline = 'middle';

    // 2026.02.12 : 박은정 : 사용 가능한 텍스트 영역
    const maxWidth = Math.max(1, w - padding * 2);
    const lineHeightPx = fontSize * lineHeight;

    // 2026.02.12 : 박은정 : 줄바꿈 처리 및 자동 줄바꿈
    const lines = this.wrapText(ctx, text, maxWidth);

    // 2026.04.23 : 박은정 : 총 텍스트 높이 및 첫 줄의 세로 중앙 좌표 계산
    const totalTextHeight = lines.length * lineHeightPx;
    const blockTop = (h - totalTextHeight) / 2;
    const firstLineMidY = blockTop + lineHeightPx / 2;

    // 2026.02.12 : 박은정 : 텍스트 정렬에 따른 X 좌표 결정
    let xPos: number;
    if (textAlign === 'left') {
      xPos = padding;
    } else if (textAlign === 'right') {
      xPos = w - padding;
    } else {
      xPos = w / 2;
    }

    // 2026.02.12 : 박은정 : 각 줄 렌더링 (Y는 각 줄의 수직 중심)
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], xPos, firstLineMidY + i * lineHeightPx);
    }

    // 2026.02.12 : 박은정 : 텍스처 업데이트
    if (this.canvasTexture) {
      this.canvasTexture.needsUpdate = true;
    }
  }

  /**
   * 2026.02.12 : 박은정 : 배경색이 완전 투명인지 확인
   * - rgba(0,0,0,0), rgba(255,255,255,0) 등 알파값이 0인 경우 true
   * - 'transparent' 키워드도 true
   */
  private isFullyTransparent(color: string): boolean {
    if (!color) return true;
    if (color === 'transparent') return true;
    // 2026.02.12 : 박은정 : rgba에서 알파값 확인
    const rgbaMatch = color.match(/rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/);
    if (rgbaMatch && parseFloat(rgbaMatch[1]) === 0) return true;
    // 2026.02.12 : 박은정 : #RRGGBBAA 형식에서 알파값 확인
    if (color.length === 9 && color.startsWith('#') && color.endsWith('00')) return true;
    return false;
  }

  /**
   * 2026.02.12 : 박은정 : 텍스트 줄바꿈 처리
   * - \n으로 명시적 줄바꿈
   * - 캔버스 폭 초과 시 자동 줄바꿈
   *
   * @param ctx Canvas 2D 컨텍스트
   * @param text 원본 텍스트
   * @param maxWidth 최대 폭 (px)
   * @returns 줄 배열
   */
  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const result: string[] = [];

    // 2026.02.12 : 박은정 : 명시적 줄바꿈 먼저 처리
    const paragraphs = text.split('\n');

    for (const paragraph of paragraphs) {
      if (paragraph.trim() === '') {
        result.push('');
        continue;
      }

      // 2026.02.12 : 박은정 : 단어 단위 자동 줄바꿈
      const words = paragraph.split(' ');
      let currentLine = '';

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && currentLine !== '') {
          result.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine) {
        result.push(currentLine);
      }
    }

    return result;
  }
}

// ============================================================================
// 컴포넌트 팩토리 등록
// ============================================================================

export const quadTextPlaneType = 'mp.quadTextPlane';
export function makeQuadTextPlane() {
  return new QuadTextPlane();
}

